import React from 'react'
import PropTypes from 'prop-types'
import styles from './index.module.scss'
import ARChart from '../ar-chart-component'
import SummaryCards from '../summary-component/index.js'
import { COLORS, INVOICE_STATUS_COLORS } from '../../../utilities/constants'

function ARDashboardCharts ({
  data,
  isFetching,
  fakeCount,
  onPageRequest,
  monthlyData
}) {
  const getStatusColor = status => {
    switch (status) {
      case 'OVERDUE':
        return INVOICE_STATUS_COLORS.OVERDUE
      case 'PAID':
        return INVOICE_STATUS_COLORS.PAID
      case 'OPEN':
        return INVOICE_STATUS_COLORS.OPEN
      case 'VOIDED':
        return INVOICE_STATUS_COLORS.VOIDED
      case 'CLOSED_IN_QBO':
        return INVOICE_STATUS_COLORS.CLOSED_IN_QBO
      default:
        return '#000000'
    }
  }

  const getStatusLabel = status => {
    switch (status) {
      case 'OVERDUE':
        return 'Overdue'
      case 'PAID':
        return 'Paid'
      case 'OPEN':
        return 'Open'
      case 'VOIDED':
        return 'Voided'
      case 'CLOSED_IN_QBO':
        return 'Closed in QBO'
      default:
        return ''
    }
  }

  return (
    <div className={styles.chartsContainer}>
      <div className={styles.chartRow}>
        <div className={styles.summaryContainer}>
          <SummaryCards
            totalSales={data?.summaryMetrics?.totalSales}
            accountsReceivable={data?.summaryMetrics?.accountsReceivable}
            overdueReceivables={data?.summaryMetrics?.overdueReceivables}
            percentOverdue={data?.summaryMetrics?.percentOverdue}
          />
        </div>
        <div className={styles.gaugeContainer}>
          <ARChart
            title='Overdue Percentage'
            type='gauge'
            gaugeValue={data.summaryMetrics.percentOverdue}
            gaugeMax={100}
            series={[{ data: [] }]}
            height={250}
          />
        </div>
      </div>
      <div className={styles.chartRow}>
        <div className={styles.histogramContainer}>
          {!isFetching
            ? (
              <ARChart
                title='Monthly Overdue'
                type='column'
                categories={monthlyData?.nodes?.map(item => `${item.month} ${item.year}`)}
                series={[
                  {
                    name: 'Total Invoice Amount',
                    data: monthlyData?.nodes?.map(item => item.total),
                    color: COLORS?.WARNING
                  },
                  {
                    name: 'Total Outstanding Amount',
                    data: monthlyData?.nodes?.map(item => item.outstanding),
                    color: COLORS?.PRIMARY_DK
                  },
                  {
                    name: 'Total Overdue Amount',
                    data: monthlyData?.nodes?.map(item => item.overdue),
                    color: COLORS?.SECONDARY
                  }
                ]}
                height={370}
                options={{
                  plugins: {
                    legend: {
                      display: true,
                      position: 'bottom'
                    }
                  },
                  scales: {
                    x: {
                      barPercentage: 0.5,
                      categoryPercentage: 0.6
                    },
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function (value) {
                          return value.toLocaleString()
                        }
                      }
                    }
                  }
                }}
                fakeCount={fakeCount}
                onPageRequest={onPageRequest}
                pageInfo={monthlyData?.pageInfo}
                totalCount={monthlyData?.totalCount}
              />
              )
            : (
              <div className={styles.noDataContainer}>Loading...</div>
              )}
        </div>
        <div className={styles.gaugeContainer}>
          {data?.overdueByCollector.length
            ? <ARChart
                title='Overdue Balance By Collector'
                type='pie'
                series={[
                  {
                    data: data.overdueByCollector.map(item => item.amount)
                  }
                ]}
                labels={data.overdueByCollector.map(item => item.name)}
                height={370}
              />
            : <div className={styles.noDataContainer}>No Overdue Balance Found</div>}
        </div>
      </div>

      <div className={styles.chartRow}>
        <div className={styles.barChartContainer}>
          <ARChart
            title={'Top 5 Accounts By Sales'}
            type='bar'
            horizontal
            categories={data.topAccountsBySales.barGraph.map(item => item.name)}
            series={[
              {
                name: 'Sales Amount',
                data: data.topAccountsBySales.barGraph.map(item => item.amount),
                color: COLORS?.PRIMARY
              }
            ]}
            height={210}
          />
        </div>
        <div className={styles.barChartContainer}>
          {data?.topAccountsBySales?.pieChart?.topPercent
            ? <ARChart
                title={'Top 5 Accounts By Sales'}
                type='pie'
                series={[
                  {
                    data: [data.topAccountsBySales.pieChart.topAmount, data.topAccountsBySales.pieChart.restAmount]
                  }
                ]}
                labels={['Top 5 Accounts', 'Others']}
                height={210}
                options={{
                  colors: [COLORS?.PRIMARY, COLORS?.PRIMARY_DK]
                }}
              />
            : <div className={styles.noDataContainer}>No Accounts By Sales Found</div>}
        </div>
      </div>
      <div className={styles.chartRow}>
        <div className={styles.barChartContainer}>
          <ARChart
            title='Top 5 Accounts By Overdue Receivables'
            type='bar'
            horizontal
            categories={data.topAccountsByOverdue.barGraph.map(item => item.name)}
            series={[
              {
                name: 'Overdue Amount',
                data: data.topAccountsByOverdue.barGraph.map(item => item.amount),
                color: COLORS?.PRIMARY
              }
            ]}
            height={210}
          />
        </div>
        <div className={styles.barChartContainer}>
          {data.topAccountsByOverdue.pieChart.topPercent
            ? <ARChart
                title={'Top 5 Accounts By Overdue Receivables'}
                type='pie'
                series={[
                  {
                    data: [data.topAccountsByOverdue.pieChart.topAmount, data.topAccountsByOverdue.pieChart.restAmount]
                  }
                ]}
                labels={['Top 5 Accounts', 'Others']}
                height={210}
                options={{
                  colors: [COLORS?.PRIMARY, COLORS?.PRIMARY_DK]
                }}
              />
            : <div className={styles.noDataContainer}>No Accounts By Overdue Found</div>}
        </div>
      </div>
      <div className={styles.chartRow}>
        <div className={styles.barChartContainer}>
          <ARChart
            title='Aging Buckets By Overdue Amount'
            type='bar'
            horizontal
            categories={data.agingSummary.map(item => item.period)}
            series={[
              {
                name: 'Overdue Amount',
                data: data.agingSummary.map(item => item.amount)
              }
            ]}
            height={250}
          />
        </div>
        <div className={styles.pieContainer}>
          {data?.invoiceStatusPercent?.length
            ? <ARChart
                title='Invoice Status Tracker'
                type='pie'
                series={[
                  {
                    data: data?.invoiceStatusPercent?.map(item => item.count)
                  }
                ]}
                labels={data?.invoiceStatusPercent?.map(item => getStatusLabel(item.status))}
                height={250}
                options={{
                  colors: data?.invoiceStatusPercent?.map(item => getStatusColor(item.status))
                }}
              />
            : <div className={styles.noDataContainer}>No Invoice Data Found</div>}
        </div>
      </div>
    </div>
  )
}

ARDashboardCharts.propTypes = {
  data: PropTypes.shape({
    invoiceStatusPercent: PropTypes.arrayOf(
      PropTypes.shape({
        count: PropTypes.number.isRequired,
        percent: PropTypes.number.isRequired,
        status: PropTypes.string.isRequired
      })
    ),
    summaryMetrics: PropTypes.arrayOf(
      PropTypes.shape({
        accountsReceivable: PropTypes.number.isRequired,
        overdueReceivables: PropTypes.number.isRequired,
        percentOverdue: PropTypes.number.isRequired,
        totalSales: PropTypes.number.isRequired
      })
    ),
    topAccountsBySales: PropTypes.shape({
      barGraph: PropTypes.arrayOf(
        PropTypes.shape({
          amount: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired
        })
      ),
      pieChart: PropTypes.shape({
        restAmount: PropTypes.number.isRequired,
        restPercent: PropTypes.number.isRequired,
        topAmount: PropTypes.number.isRequired,
        topPercent: PropTypes.number.isRequired
      })
    }),
    topAccountsByOverdue: PropTypes.shape({
      barGraph: PropTypes.arrayOf(
        PropTypes.shape({
          amount: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired
        })
      ),
      pieChart: PropTypes.shape({
        restAmount: PropTypes.number.isRequired,
        restPercent: PropTypes.number.isRequired,
        topAmount: PropTypes.number.isRequired,
        topPercent: PropTypes.number.isRequired
      })
    }),
    overdueByCollector: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        percent: PropTypes.number.isRequired
      })
    ),
    monthlyData: PropTypes.arrayOf(
      PropTypes.shape({
        month: PropTypes.string.isRequired,
        outstanding: PropTypes.number.isRequired,
        overdue: PropTypes.number.isRequired,
        total: PropTypes.number.isRequired,
        year: PropTypes.number.isRequired
      })
    ),
    agingSummary: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number.isRequired,
        period: PropTypes.string.isRequired
      })
    )
  }),
  monthlyData: PropTypes.shape({
    nodes: PropTypes.arrayOf(PropTypes.object).isRequired,
    pageInfo: PropTypes.object,
    totalCount: PropTypes.number
  }),
  isFetching: PropTypes.bool.isRequired,
  fakeCount: PropTypes.shape({
    start: PropTypes.number.isRequired,
    end: PropTypes.number.isRequired
  }),
  onPageRequest: PropTypes.func.isRequired
}

export default ARDashboardCharts
